import { useEffect, useRef, useState } from 'react'
import { Button, Text } from '@mantine/core'
import type {
  BriefingAction,
  BriefingAppointment,
  BriefingDay,
  BriefingItem,
} from '../../hooks/useBriefingDay'
import { BriefingPatientCard } from './BriefingPatientCard'
import {
  doneKey,
  formatWhen,
  KIND_RANK,
  SOURCE_LABELS,
  urgency,
  URGENCY_RANK,
} from './briefingCopy'

type BriefingAttentionProps = {
  items: BriefingItem[]
  hiddenCount: number
  appointmentsById: Map<string, BriefingAppointment>
  sources: BriefingDay['sources']
  generatedAt: string
  focusName: string | null
  // The items were narrowed to the patients a greeting chip shows.
  filteredByChip: boolean
  // The provider's local date ('YYYY-MM-DD').
  today: string
  selectedAppointmentId: string | null
  onSelectVisit: (appointmentId: string | null) => void
  // In-place actions done on this screen, by item version (doneKey).
  done: Map<string, { did: string; canUndo: boolean }>
  quickBusyId: string | null
  onQuick: (item: BriefingItem) => void
  onUndoQuick: (item: BriefingItem) => void
  onShowHidden: () => void
  onOpen: (href: string) => void
  onAction: (item: BriefingItem, change: BriefingAction) => void
}

// Patients shown before "View all N items".
const PATIENTS_SHOWN = 3

type PatientGroup = {
  patientId: string
  items: BriefingItem[]
  appointment: BriefingAppointment | undefined
}

/**
 * The right column: one card per patient, each concern its own row and action, the most urgent
 * patients first (urgency, then visit time). Done work sinks to the end of its card.
 */
export function BriefingAttention({
  items,
  hiddenCount,
  appointmentsById,
  sources,
  generatedAt,
  focusName,
  filteredByChip,
  today,
  selectedAppointmentId,
  onSelectVisit,
  done,
  quickBusyId,
  onQuick,
  onUndoQuick,
  onShowHidden,
  onOpen,
  onAction,
}: BriefingAttentionProps) {
  const unavailable = sources.filter((s) => s.status === 'unavailable')
  const [showAll, setShowAll] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const hiddenButtonRef = useRef<HTMLButtonElement>(null)
  // A hidden concern unmounts with its menu, so focus would fall to <body>: hand it to the next
  // concern's menu button, else the hidden-items button.
  const refocus = useRef<{ goneId: string; nextId: string | undefined } | null>(null)

  useEffect(() => {
    const pending = refocus.current
    refocus.current = null
    if (!pending || items.some((item) => item.id === pending.goneId)) return
    const next = pending.nextId
      ? rootRef.current?.querySelector<HTMLElement>(
          `[data-briefing-menu="${CSS.escape(pending.nextId)}"]`,
        )
      : null
    ;(next ?? hiddenButtonRef.current)?.focus()
  }, [items])

  const isDone = (item: BriefingItem) => done.has(doneKey(item))
  const start = (item: BriefingItem) =>
    (item.appointmentId && appointmentsById.get(item.appointmentId)?.start) || '9999'
  // Within one urgency, a critical finding still goes before a high one, before the rest.
  const severity = (item: BriefingItem) =>
    item.severity === 'critical' ? 0 : item.severity === 'high' ? 1 : 2
  const byUrgency = (a: BriefingItem, b: BriefingItem) =>
    Number(isDone(a)) - Number(isDone(b)) ||
    URGENCY_RANK[urgency(a, today)] - URGENCY_RANK[urgency(b, today)] ||
    severity(a) - severity(b) ||
    KIND_RANK[a.kind] - KIND_RANK[b.kind] ||
    start(a).localeCompare(start(b))

  const groupsById = new Map<string, PatientGroup>()
  for (const item of [...items].sort(byUrgency)) {
    const group = groupsById.get(item.patient.id) ?? {
      patientId: item.patient.id,
      items: [],
      appointment: undefined,
    }
    group.items.push(item)
    group.appointment ??= item.appointmentId ? appointmentsById.get(item.appointmentId) : undefined
    groupsById.set(item.patient.id, group)
  }
  // A patient booked twice shows the visit the provider selected.
  const selectedVisit = selectedAppointmentId
    ? appointmentsById.get(selectedAppointmentId)
    : undefined
  const selectedGroup = selectedVisit?.patient && groupsById.get(selectedVisit.patient.id)
  if (selectedGroup) selectedGroup.appointment = selectedVisit
  // Each group is already in urgency order, so its first item ranks the patient.
  const groups = [...groupsById.values()].sort((a, b) => byUrgency(a.items[0], b.items[0]))
  const capped = !showAll && !selectedAppointmentId && groups.length > PATIENTS_SHOWN
  const shown = capped ? groups.slice(0, PATIENTS_SHOWN) : groups
  const openCount = items.filter((item) => !isDone(item)).length

  const act = (item: BriefingItem, change: BriefingAction) => {
    if (change.action !== 'restore') {
      const flat = shown.flatMap((g) => g.items)
      const at = flat.findIndex((i) => i.id === item.id)
      refocus.current = { goneId: item.id, nextId: (flat[at + 1] ?? flat[at - 1])?.id }
    }
    onAction(item, change)
  }

  return (
    <div ref={rootRef} className="flex min-w-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2.5 gap-y-1">
        <h4 className="text-sm font-bold text-gray-900">
          {focusName ? `Needs your attention · ${focusName}` : 'Needs your attention'}
        </h4>
        <span className="text-[13px] text-gray-600">
          Most urgent first · acting here updates the source
        </span>
      </div>

      {items.length === 0 ? (
        unavailable.length > 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {focusName
              ? `Nothing found for ${focusName} in the sources that loaded.`
              : filteredByChip
                ? 'Nothing found for these patients in the sources that loaded.'
                : 'Nothing found in the sources that loaded.'}
          </Text>
        ) : (
          <div className="py-4 text-center">
            <p className="text-sm font-semibold text-gray-900">No items pending review</p>
            <p className="mt-1 text-[13px] text-gray-600">
              {focusName
                ? 'New items for this patient will appear here when available.'
                : 'New items will appear here when available.'}
            </p>
          </div>
        )
      ) : (
        <ol className="grid gap-2.5">
          {shown.map((group) => (
            <li key={group.patientId}>
              <BriefingPatientCard
                items={group.items}
                appointment={group.appointment}
                today={today}
                selected={
                  !!group.appointment && group.appointment.appointmentId === selectedAppointmentId
                }
                onSelectVisit={onSelectVisit}
                onOpen={onOpen}
                onAction={act}
                done={done}
                quickBusyId={quickBusyId}
                onQuick={onQuick}
                onUndoQuick={onUndoQuick}
              />
            </li>
          ))}
        </ol>
      )}
      {capped && (
        <Button
          variant="default"
          size="sm"
          radius="md"
          className="self-start"
          onClick={() => setShowAll(true)}
        >
          View all {openCount} {openCount === 1 ? 'item' : 'items'}
        </Button>
      )}
      {showAll && groups.length > PATIENTS_SHOWN && !selectedAppointmentId && (
        <Button
          variant="subtle"
          size="compact-sm"
          className="self-start"
          onClick={() => setShowAll(false)}
        >
          Show fewer
        </Button>
      )}

      {unavailable.map((source) => (
        <Text key={source.kind} size="xs" c="red">
          {SOURCE_LABELS[source.kind] ?? source.kind} unavailable right now
        </Text>
      ))}
      <div className="flex flex-wrap items-center gap-x-2 text-[13px] text-gray-600">
        <span>Updated {formatWhen(generatedAt)}</span>
        {hiddenCount > 0 && (
          <Button ref={hiddenButtonRef} variant="subtle" size="compact-xs" onClick={onShowHidden}>
            {hiddenCount} hidden · Dismissed & reminders
          </Button>
        )}
      </div>
    </div>
  )
}
